import { ReferenceField } from '@/components/admin/reglages/reference-list';
import { OPERATOR_TOKEN_LABEL_MAX } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * « Frapper un jeton » - a label, and nothing else (§20.2, issue #84).
 *
 * The only thing minting asks for is a label, because WHOSE token this is
 * comes from the cookie and never from a form: a screen able to name the owner
 * would be a screen able to mint somebody else a key, which is the one thing
 * these routes must be unable to do. So this form has one field.
 *
 * ## The label is required, and bounded at sixty
 *
 * `operatorTokenCreateRequestSchema` says `min(1).max(OPERATOR_TOKEN_LABEL_MAX)`,
 * so an empty send leaves as a 400 and a long one too. The button says the
 * first before the click and `maxLength` says the second while typing, at the
 * régime of `ReferenceField` - which is reused rather than respelled, for the
 * reason the links form reuses it. The bound itself comes from `shared`: spelt
 * a second time here, it would be the copy that drifts, and an operator would
 * type a label the server throws away.
 *
 * The placeholder shows the SHAPE of a good label rather than repeating the
 * name of the field: a label is read in a list months later, next to a date and
 * a last use, and « Claude Code sur mon poste » is what makes a row nameable
 * when the time comes to revoke it. « jeton 2 » does not.
 *
 * ## The draft is reset by a REMOUNT
 *
 * The régime of `reglages.personas.tsx` and of the links form: the component
 * owns its draft, initialised from nothing and never repaired by an effect,
 * and the page gives it a new key when a mint LANDS. A refused mint keeps what
 * was typed - the refusal is precisely what the operator is about to fix.
 */

export interface TokenPanelProps {
  readonly submitting: boolean;
  /** A transport failure, worded by the page. Null when there is none. */
  readonly failure: string | null;
  readonly onSubmit: (label: string) => void;
}

export function TokenPanel({ submitting, failure, onSubmit }: TokenPanelProps) {
  const [label, setLabel] = useState('');
  const armed = label.trim() !== '' && !submitting;

  return (
    <form
      data-testid="jetons-panneau"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (armed) onSubmit(label.trim());
      }}
      className="space-y-4"
    >
      <div className="max-w-xl">
        <ReferenceField
          label="Libellé"
          testId="jetons-libelle"
          value={label}
          max={OPERATOR_TOKEN_LABEL_MAX}
          placeholder="Claude Code sur mon poste"
          onChange={setLabel}
        />
        <p className="pt-1 text-caption text-gray-500">
          Pour vous : il nomme le poste ou l’usage, et c’est à lui que vous reconnaîtrez le jeton le
          jour où vous le révoquerez.
        </p>
      </div>

      {failure === null ? null : (
        <p role="alert" className="text-rouge-deep text-small">
          {failure}
        </p>
      )}

      <Button variant="secondary" type="submit" data-testid="jetons-frapper" disabled={!armed}>
        {submitting ? 'Frappe…' : 'Frapper le jeton'}
      </Button>
    </form>
  );
}
